import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signIn, signOut, fetchUserAttributes, resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import '../App.css';

function Login() {
  const [view,        setView]        = useState('login');
  const [email,       setEmail]       = useState('');
  const [password,    setPassword]    = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPw,   setConfirmPw]   = useState('');
  const [code,        setCode]        = useState('');
  const [showPw,      setShowPw]      = useState(false);
  const [showNewPw,   setShowNewPw]   = useState(false);
  const [error,       setError]       = useState('');
  const [loading,     setLoading]     = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      try { await signOut(); } catch {}
      await signIn({ username: email, password });
      const attrs = await fetchUserAttributes();
      const role  = attrs['custom:role'];
      navigate(role === 'worker' || role === 'both' ? '/dashboard' : '/client-dashboard');
    } catch (err) {
      const code = err.name || err.code || '';
      if (code === 'UserNotConfirmedException' || err.message?.includes('not confirmed') || err.message?.includes('User is not confirmed')) {
        setError('No verified account found for this email. Please sign up and complete verification.');
      } else if (code === 'NotAuthorizedException' || code === 'UserNotFoundException' || err.message?.includes('Incorrect username or password') || err.message?.includes('User does not exist') || err.message?.includes('needs to be authenticated')) {
        setError('Incorrect email or password. Please try again.');
      } else {
        setError(err.message || 'Login failed. Please try again.');
      }
    }
    setLoading(false);
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    if (!email.trim()) { setError('Please enter your email address first.'); return; }
    setError(''); setLoading(true);
    try {
      await resetPassword({ username: email });
      setView('reset');
    } catch (err) {
      setError(err.message || 'Could not send reset code. Check your email and try again.');
    }
    setLoading(false);
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPw) { setError('Passwords do not match.'); return; }
    if (newPassword.length < 8)    { setError('Password must be at least 8 characters.'); return; }
    setError(''); setLoading(true);
    try {
      await confirmResetPassword({ username: email, confirmationCode: code, newPassword });
      setView('done');
    } catch (err) {
      setError(err.message || 'Reset failed. Check your code and try again.');
    }
    setLoading(false);
  };

  const pwStrength = (pw) => {
    let s = 0;
    if (pw.length >= 8) s++;
    if (/[A-Z]/.test(pw)) s++;
    if (/[0-9]/.test(pw)) s++;
    if (/[^A-Za-z0-9]/.test(pw)) s++;
    return s;
  };
  const strength      = pwStrength(newPassword);
  const strengthLabel = ['', 'Weak', 'Fair', 'Good', 'Strong'][strength];
  const strengthColor = ['', '#dc2626', '#d97706', '#2563eb', '#10b981'][strength];

  /* ── Shared styles ── */
  const inputSt = {
    width: '100%', background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)', border: '1.5px solid rgba(255,255,255,0.25)',
    borderRadius: '10px', padding: '0.825rem 1rem', color: 'white',
    fontSize: '0.925rem', outline: 'none', fontFamily: 'DM Sans, sans-serif',
    transition: 'all 0.2s', boxSizing: 'border-box',
  };
  const labelSt = {
    display: 'block', fontSize: '0.72rem', fontWeight: 700,
    color: 'rgba(255,255,255,0.65)', marginBottom: '0.45rem',
    letterSpacing: '0.06em', textTransform: 'uppercase',
  };
  const btnPrimary = {
    width: '100%', padding: '0.9rem', background: 'rgba(255,255,255,0.2)',
    backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
    border: '1.5px solid rgba(255,255,255,0.4)', borderRadius: '11px',
    color: 'white', fontSize: '0.95rem', fontWeight: 700, cursor: 'pointer',
    fontFamily: 'DM Sans, sans-serif', transition: 'all 0.2s', letterSpacing: '0.01em',
    marginTop: '0.25rem',
  };

  const WALLPAPER = {
    minHeight: '100vh',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '1.5rem',
    backgroundImage: 'url(https://images.unsplash.com/photo-1505118380757-91f5f5632de0?w=1800&q=85&auto=format&fit=crop)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    fontFamily: 'DM Sans, sans-serif',
    position: 'relative',
  };

  const GLASS_CARD = {
    width: '100%', maxWidth: 420,
    background: 'rgba(255,255,255,0.08)',
    backdropFilter: 'blur(40px) saturate(200%) brightness(1.1)',
    WebkitBackdropFilter: 'blur(40px) saturate(200%) brightness(1.1)',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '28px',
    overflow: 'hidden',
    boxShadow: '0 32px 80px rgba(0,0,0,0.45), 0 8px 32px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.3)',
  };

  const CARD_HEADER = {
    padding: '2rem 2.25rem 1.75rem',
    position: 'relative', overflow: 'hidden',
    borderBottom: '1px solid rgba(255,255,255,0.12)',
  };

  const CARD_BODY = { padding: '1.75rem 2.25rem 2.25rem' };

  const headerTitle = (title, sub) => (
    <div style={{ position: 'relative', zIndex: 1 }}>
      {/* Glow orb */}
      <div style={{ position: 'absolute', top: -60, right: -60, width: 200, height: 200, background: 'radial-gradient(circle, rgba(61,189,194,0.15) 0%, transparent 65%)', pointerEvents: 'none' }} />
      <Link to="/" style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.45rem', fontWeight: 800, color: 'white', textDecoration: 'none', display: 'block', marginBottom: '1.25rem', letterSpacing: '-0.02em', textShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
        Vyb<span style={{ color: 'rgba(255,255,255,0.7)' }}>lo</span>
      </Link>
      <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.5rem', fontWeight: 800, color: 'white', marginBottom: '0.2rem', textShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>{title}</h1>
      <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.875rem' }}>{sub}</p>
    </div>
  );

  return (
    <div style={WALLPAPER}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(5,25,40,0.45)', backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)', pointerEvents: 'none' }} />
      <div style={{ width: '100%', maxWidth: 420, display: 'flex', flexDirection: 'column', gap: '1.25rem', position: 'relative', zIndex: 1 }}>

        {/* ── CARD ── */}
        <div style={GLASS_CARD}>

          {/* Header */}
          <div style={CARD_HEADER}>
            {view === 'login'  && headerTitle('Welcome back', 'Sign in to your Vyblo account')}
            {view === 'forgot' && headerTitle('Forgot password?', "Enter your email and we'll send a reset code")}
            {view === 'reset'  && headerTitle('Set new password', `Check ${email} for your code`)}
            {view === 'done'   && headerTitle('Password reset!', 'You can now sign in with your new password')}
          </div>

          {/* Body */}
          <div style={CARD_BODY}>

            {/* Error */}
            {error && (
              <div style={{ background: 'rgba(220,38,38,0.15)', border: '1px solid rgba(220,38,38,0.35)', borderRadius: '10px', padding: '0.75rem 1rem', fontSize: '0.82rem', color: '#fca5a5', marginBottom: '1.25rem', display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                <span style={{ flexShrink: 0 }}>⚠️</span>
                <span>
                  {error}
                  {error.includes('Please sign up') && <> <Link to="/signup" style={{ color: '#fca5a5', fontWeight: 700 }}>Sign up here →</Link></>}
                </span>
              </div>
            )}

            {/* ── LOGIN ── */}
            {view === 'login' && (
              <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div>
                  <label style={labelSt}>Email</label>
                  <input type="email" placeholder="you@gmail.com" value={email} onChange={e => setEmail(e.target.value)} required autoComplete="email"
                    style={inputSt}
                    onFocus={e => { e.target.style.borderColor = 'rgba(255,255,255,0.6)'; e.target.style.background = 'rgba(255,255,255,0.18)'; }}
                    onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.25)'; e.target.style.background = 'rgba(255,255,255,0.12)'; }}
                  />
                </div>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.45rem' }}>
                    <label style={{ ...labelSt, marginBottom: 0 }}>Password</label>
                    <button type="button" onClick={() => { setView('forgot'); setError(''); }}
                      style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.65)', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', padding: 0, transition: 'color 0.2s' }}
                      onMouseEnter={e => e.currentTarget.style.color = 'white'}
                      onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.65)'}>
                      Forgot password?
                    </button>
                  </div>
                  <div style={{ position: 'relative' }}>
                    <input type={showPw ? 'text' : 'password'} placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} required autoComplete="current-password"
                      style={{ ...inputSt, paddingRight: '2.75rem' }}
                      onFocus={e => { e.target.style.borderColor = 'rgba(255,255,255,0.6)'; e.target.style.background = 'rgba(255,255,255,0.18)'; }}
                      onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.25)'; e.target.style.background = 'rgba(255,255,255,0.12)'; }}
                    />
                    <button type="button" onClick={() => setShowPw(!showPw)}
                      style={{ position: 'absolute', right: '0.875rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem', padding: 0 }}>
                      {showPw ? '🙈' : '👁'}
                    </button>
                  </div>
                </div>
                <button type="submit" disabled={loading} style={btnPrimary}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.3)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.2)'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                  {loading ? 'Signing in...' : 'Sign In →'}
                </button>
              </form>
            )}

            {/* ── FORGOT ── */}
            {view === 'forgot' && (
              <form onSubmit={handleForgot} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div>
                  <label style={labelSt}>Email address</label>
                  <input type="email" placeholder="you@gmail.com" value={email} onChange={e => setEmail(e.target.value)} required autoComplete="email"
                    style={inputSt}
                    onFocus={e => { e.target.style.borderColor = 'rgba(255,255,255,0.6)'; e.target.style.background = 'rgba(255,255,255,0.18)'; }}
                    onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.25)'; e.target.style.background = 'rgba(255,255,255,0.12)'; }}
                  />
                </div>
                <div style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '10px', padding: '0.75rem 1rem', fontSize: '0.78rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.55 }}>
                  📧 We'll send a 6-digit code to this email. Check your spam folder if you don't see it.
                </div>
                <button type="submit" disabled={loading} style={btnPrimary}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}>
                  {loading ? 'Sending code...' : 'Send Reset Code →'}
                </button>
                <button type="button" onClick={() => { setView('login'); setError(''); }}
                  style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.55)', fontSize: '0.875rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', padding: '0.25rem 0', transition: 'color 0.2s' }}
                  onMouseEnter={e => e.currentTarget.style.color = 'white'}
                  onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.55)'}>
                  ← Back to sign in
                </button>
              </form>
            )}

            {/* ── RESET ── */}
            {view === 'reset' && (
              <form onSubmit={handleReset} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div>
                  <label style={labelSt}>6-digit code</label>
                  <input type="text" placeholder="123456" value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} required maxLength={6}
                    style={{ ...inputSt, letterSpacing: '0.3em', fontSize: '1.2rem', textAlign: 'center', fontWeight: 700 }}
                    onFocus={e => { e.target.style.borderColor = 'rgba(255,255,255,0.6)'; e.target.style.background = 'rgba(255,255,255,0.18)'; }}
                    onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.25)'; e.target.style.background = 'rgba(255,255,255,0.12)'; }}
                  />
                </div>
                <div>
                  <label style={labelSt}>New password</label>
                  <div style={{ position: 'relative' }}>
                    <input type={showNewPw ? 'text' : 'password'} placeholder="••••••••" value={newPassword} onChange={e => setNewPassword(e.target.value)} required autoComplete="new-password"
                      style={{ ...inputSt, paddingRight: '2.75rem' }}
                      onFocus={e => { e.target.style.borderColor = 'rgba(255,255,255,0.6)'; e.target.style.background = 'rgba(255,255,255,0.18)'; }}
                      onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.25)'; e.target.style.background = 'rgba(255,255,255,0.12)'; }}
                    />
                    <button type="button" onClick={() => setShowNewPw(!showNewPw)}
                      style={{ position: 'absolute', right: '0.875rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem', padding: 0 }}>
                      {showNewPw ? '🙈' : '👁'}
                    </button>
                  </div>
                  {newPassword.length > 0 && (
                    <div style={{ marginTop: '0.5rem' }}>
                      <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '0.3rem' }}>
                        {[1,2,3,4].map(i => (
                          <div key={i} style={{ flex: 1, height: '3px', borderRadius: '99px', background: i <= strength ? strengthColor : 'rgba(255,255,255,0.15)', transition: 'background 0.2s' }} />
                        ))}
                      </div>
                      <p style={{ fontSize: '0.72rem', color: strengthColor, fontWeight: 600 }}>{strengthLabel}</p>
                    </div>
                  )}
                </div>
                <div>
                  <label style={labelSt}>Confirm new password</label>
                  <input type="password" placeholder="••••••••" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} required autoComplete="new-password"
                    style={{ ...inputSt, borderColor: confirmPw && confirmPw !== newPassword ? 'rgba(220,38,38,0.6)' : 'rgba(255,255,255,0.25)' }}
                    onFocus={e => { e.target.style.borderColor = 'rgba(255,255,255,0.6)'; e.target.style.background = 'rgba(255,255,255,0.18)'; }}
                    onBlur={e => { e.target.style.borderColor = confirmPw && confirmPw !== newPassword ? 'rgba(220,38,38,0.6)' : 'rgba(255,255,255,0.25)'; e.target.style.background = 'rgba(255,255,255,0.12)'; }}
                  />
                  {confirmPw && confirmPw !== newPassword && <p style={{ fontSize: '0.72rem', color: '#fca5a5', marginTop: '0.3rem' }}>Passwords don't match</p>}
                </div>
                <button type="submit" disabled={loading || newPassword !== confirmPw || newPassword.length < 8} style={{ ...btnPrimary, opacity: (loading || newPassword !== confirmPw || newPassword.length < 8) ? 0.5 : 1 }}
                  onMouseEnter={e => { if (!loading && newPassword === confirmPw && newPassword.length >= 8) e.currentTarget.style.background = 'rgba(255,255,255,0.3)'; }}
                  onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}>
                  {loading ? 'Resetting...' : 'Set New Password →'}
                </button>
                <button type="button" onClick={() => { setView('forgot'); setError(''); setCode(''); setNewPassword(''); setConfirmPw(''); }}
                  style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.55)', fontSize: '0.875rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', padding: '0.25rem 0', transition: 'color 0.2s' }}
                  onMouseEnter={e => e.currentTarget.style.color = 'white'}
                  onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.55)'}>
                  ← Resend code
                </button>
              </form>
            )}

            {/* ── DONE ── */}
            {view === 'done' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', textAlign: 'center' }}>
                <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(16,185,129,0.2)', border: '2px solid rgba(16,185,129,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto', fontSize: '1.4rem', color: '#10b981' }}>✓</div>
                <p style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.65)', lineHeight: 1.6 }}>Your password has been reset. You can now sign in with your new password.</p>
                <button style={btnPrimary} onClick={() => { setView('login'); setPassword(''); setError(''); }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}>
                  Sign In Now →
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Sign up link */}
        {view === 'login' && (
          <p style={{ textAlign: 'center', fontSize: '0.875rem', color: 'rgba(255,255,255,0.55)' }}>
            Don't have an account?{' '}
            <Link to="/signup" style={{ color: 'white', fontWeight: 700, textDecoration: 'none' }}>Sign up free</Link>
          </p>
        )}

        {/* Footer branding */}
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.1rem', fontWeight: 800, color: 'rgba(255,255,255,0.7)', letterSpacing: '-0.02em' }}>Vyblo</div>
          <p style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.35)', marginTop: '0.2rem' }}>Book local services you love</p>
        </div>
      </div>
    </div>
  );
}

export default Login;